export const FIRST_SEEN_BANDS = {
    last_14_days: { label: 'Last 14 days', days: [14, null] },
    '15_to_90_days': { label: '15–90 days', days: [90, 14] },
    '3_to_12_months': { label: '3–12 months', days: [365, 90] },
    over_a_year: { label: 'Over a year', days: [null, 365] },
};
export const RATING_BANDS = {
    under_3: { label: 'Under 3', max: 2.99 },
    '3_to_3_9': { label: '3–3.9', min: 3, max: 3.99 },
    '4_to_4_4': { label: '4–4.4', min: 4, max: 4.49 },
    '4_5_and_up': { label: '4.5 and up', min: 4.5 },
    no_reviews: { label: 'No reviews', none: true },
};
export const AVAILABILITY_LABELS = {
    in_stock: 'In stock',
    out_of_stock: 'Out of stock',
    preorder: 'Pre-order',
    backorder: 'Back-order',
    discontinued: 'Discontinued',
    unknown: 'Unknown',
};
const dayMs = 86_400_000;
const daysAgo = (days, now) => new Date(now.getTime() - days * dayMs).toISOString();
/** The "Use case" or "Spec" name of an intent node id: the last path part, readable. */
export function intentNodeLabel(id) {
    const last = id.split('/').at(-1) ?? id;
    return last.replace(/^~/, '').replace(/^[a-z]+:/, '').replace(/[-_]+/g, ' ').trim() || id;
}
/** One chip's text, as the bar shows it. */
/** A category slug as people read it: "ranges-stoves" → "Ranges stoves". */
export function categoryLabel(slug) {
    const words = slug.replace(/[-_]+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}
export function chipLabel(chip) {
    switch (chip.kind) {
        case 'category':
            return `Category: ${chip.values.map(categoryLabel).join(', ')}`;
        case 'brand':
            return `Brand: ${chip.values.join(', ')}`;
        case 'price':
            return `Price: ${rangeText(chip.min, chip.max, '$')}`;
        case 'availability':
            return `Stock: ${chip.values.map((v) => AVAILABILITY_LABELS[v] ?? v).join(', ')}`;
        case 'firstSeen':
            return `First seen: ${chip.values.map((v) => FIRST_SEEN_BANDS[v]?.label ?? v).join(', ')}`;
        case 'rating':
            return `Rating: ${chip.values.map((v) => RATING_BANDS[v]?.label ?? v).join(', ')}`;
        case 'reviews':
            return `Reviews: ${rangeText(chip.min, chip.max, '')}`;
        case 'intentNode':
            return `Intent: ${chip.values.map(intentNodeLabel).join(', ')}`;
        case 'attribute':
            return `${chip.path}: ${chip.values.length > 0 ? chip.values.join(', ') : rangeText(chip.min, chip.max, '')}`;
        case 'text':
            return `“${chip.values[0] ?? ''}”`;
    }
}
function rangeText(min, max, unit) {
    if (min !== undefined && max !== undefined)
        return `${unit}${min}–${unit}${max}`;
    if (min !== undefined)
        return `${unit}${min} and up`;
    if (max !== undefined)
        return `under ${unit}${max}`;
    return 'any';
}
/** The same chip again? Kind and path, so one chip per field; its values replace the old ones. */
export function sameField(a, b) {
    return a.kind === b.kind && a.path === b.path;
}
export function withChip(chips, chip) {
    const rest = chips.filter((one) => !sameField(one, chip));
    const empty = chip.values.length === 0 && chip.min === undefined && chip.max === undefined;
    return empty ? rest : [...rest, chip];
}
export function withoutChip(chips, chip) {
    return chips.filter((one) => !sameField(one, chip));
}
/** The catalog filter the chips mean. Bands become dates and numbers at `now`. */
export function toFilter(chips, now = new Date()) {
    const filter = {};
    for (const chip of chips) {
        switch (chip.kind) {
            case 'category':
                filter.categories = chip.values;
                break;
            case 'brand':
                filter.brands = chip.values;
                break;
            case 'price':
                if (chip.min !== undefined)
                    filter.priceMin = chip.min;
                if (chip.max !== undefined)
                    filter.priceMax = chip.max;
                break;
            case 'availability':
                filter.availability = chip.values;
                break;
            case 'firstSeen': {
                // One band only: the bar keeps one value for a banded field.
                const band = FIRST_SEEN_BANDS[chip.values[0] ?? ''];
                if (band) {
                    const [after, before] = band.days;
                    if (after !== null)
                        filter.firstSeenAfter = daysAgo(after, now);
                    if (before !== null)
                        filter.firstSeenBefore = daysAgo(before, now);
                }
                break;
            }
            case 'rating': {
                const band = RATING_BANDS[chip.values[0] ?? ''];
                if (band?.none)
                    filter.reviewed = false;
                else if (band) {
                    if (band.min !== undefined)
                        filter.ratingMin = band.min;
                    if (band.max !== undefined)
                        filter.ratingMax = band.max;
                }
                break;
            }
            case 'reviews':
                if (chip.min !== undefined)
                    filter.reviewsMin = chip.min;
                if (chip.max !== undefined)
                    filter.reviewsMax = chip.max;
                break;
            case 'intentNode':
                filter.intentNodes = [...(filter.intentNodes ?? []), ...chip.values];
                break;
            case 'attribute':
                filter.attributes = [
                    ...(filter.attributes ?? []),
                    {
                        path: chip.path,
                        ...(chip.values.length > 0 ? { values: chip.values } : {}),
                        ...(chip.min !== undefined ? { min: chip.min } : {}),
                        ...(chip.max !== undefined ? { max: chip.max } : {}),
                    },
                ];
                break;
            case 'text':
                if (chip.values[0])
                    filter.text = chip.values[0];
                break;
        }
    }
    return filter;
}
/** Chips in the URL: compact JSON, so a filtered page can be shared and reloaded. */
export function chipsToParam(chips) {
    return chips.length === 0 ? null : JSON.stringify(chips);
}
export function chipsFromParam(param) {
    if (!param)
        return [];
    try {
        const parsed = JSON.parse(param);
        if (!Array.isArray(parsed))
            return [];
        return parsed.filter(isChip);
    }
    catch {
        return [];
    }
}
function isChip(value) {
    if (typeof value !== 'object' || value === null)
        return false;
    const chip = value;
    return (typeof chip.kind === 'string' &&
        typeof chip.path === 'string' &&
        Array.isArray(chip.values) &&
        chip.values.every((v) => typeof v === 'string'));
}
/** The fields the picker shows for the current facets: only fields with data behind them. */
export function pickerFields(facets) {
    if (!facets)
        return [];
    const fields = [];
    const plain = (kind, label, group, values, name) => {
        if (values.length > 0)
            fields.push({ kind, path: '', label, group, values: values.map((b) => ({ value: b.value, label: name(b.value), count: b.count })) });
    };
    plain('category', 'Category', 'Catalog', facets.categories, categoryLabel);
    plain('brand', 'Brand', 'Catalog', facets.brands, (v) => v);
    if (facets.priceMin !== null || facets.priceMax !== null)
        fields.push({ kind: 'price', path: '', label: 'Price', group: 'Catalog', values: [], range: { min: facets.priceMin, max: facets.priceMax, unit: '$' } });
    plain('availability', 'Stock', 'Catalog', facets.availability, (v) => AVAILABILITY_LABELS[v] ?? v);
    plain('firstSeen', 'First seen', 'Catalog', facets.firstSeen.filter((b) => b.count > 0), (v) => FIRST_SEEN_BANDS[v]?.label ?? v);
    const rated = facets.rating.filter((b) => b.count > 0 && b.value !== 'no_reviews');
    if (rated.length > 0)
        plain('rating', 'Rating', 'Reviews', facets.rating.filter((b) => b.count > 0), (v) => RATING_BANDS[v]?.label ?? v);
    plain('intentNode', 'Use case / Spec', 'Intents', facets.intentNodes, intentNodeLabel);
    for (const attribute of facets.attributes) {
        if (attribute.valueType === 'number') {
            if (attribute.min !== null || attribute.max !== null)
                fields.push({ kind: 'attribute', path: attribute.path, label: attribute.label, group: 'Attributes', values: [], range: { min: attribute.min, max: attribute.max, unit: attribute.unit ?? '' }, coverage: attribute.coverage });
        }
        else if (attribute.values.length > 0) {
            fields.push({ kind: 'attribute', path: attribute.path, label: attribute.label, group: 'Attributes', values: attribute.values.map((b) => ({ value: b.value, label: b.value, count: b.count })), coverage: attribute.coverage });
        }
    }
    return fields;
}
/** "Narrow by": the first fields worth offering, in the picker's order, at most `max`. */
export function narrowBy(fields, chips, max = 6) {
    const used = new Set(chips.map((c) => `${c.kind}:${c.path}`));
    return fields.filter((f) => !used.has(`${f.kind}:${f.path}`) && (f.values.length > 1 || f.range)).slice(0, max);
}
//# sourceMappingURL=catalog-filters.js.map